import cloneDeep from 'lodash/cloneDeep'
import { BookOpen, Images, LayoutGrid, MessageCircleHeart, Star } from 'lucide-react'
import { convertToNewThemeColors } from '@/lib/utils'
import {
  applyPageMainBlockSortOrder,
  applyCollectionVisibilityUpdates,
  getSeasonalReferenceHeroBlock,
  isCollectionVisibleOnStorefront,
  resolveAutomaticSeasonalFeaturedCollection,
} from './designForm.utils'
import { DEFAULT_HERO_LAYOUT, INHERIT_HERO_LAYOUT } from './storefrontStyleOptions'
import {
  buildDemoStorefrontUrl,
  buildStorefrontPreviewUrlForPublicPath,
  resolveStorefrontPreviewBaseUrl,
} from './storefrontPreviewUrl'

export {
  buildDemoStorefrontUrl,
  buildStorefrontPreviewUrlForPublicPath,
  resolveStorefrontPreviewBaseUrl,
}

export const storefrontPreviewPages = [
  { id: 'home', label: 'Home', icon: LayoutGrid, publicPath: '/' },
  { id: 'about', label: 'About', icon: BookOpen, publicPath: '/about-us' },
  { id: 'gallery', label: 'Gallery', icon: Images, publicPath: '/gallery' },
  { id: 'contact', label: 'Contact', icon: MessageCircleHeart, publicPath: '/contact-us' },
  { id: 'reviews', label: 'Reviews', icon: Star, publicPath: '/reviews' },
]

const storefrontPreviewPageIds = new Set(storefrontPreviewPages.map((page) => page.id))

export function applyCollectionVisibilityUpdatesToPreview(
  collections = [],
  visibilityUpdates = []
) {
  return applyCollectionVisibilityUpdates(collections, visibilityUpdates)
}

const SECTION_OVERRIDE_PATHS = {
  header_banner: ['pages', '/', 'blocks', 'header', 'banner'],
  header_nav_menu: ['pages', '/', 'blocks', 'header', 'navMenu'],
  hero: ['pages', '/', 'blocks', 'hero', 'hero'],
  featured_content: ['pages', '/', 'blocks', 'main'],
}

export const LIVE_PATCHABLE_PREVIEW_STYLE_PATHS = [
  'brand.background',
  'brand.foreground',
  'brand.secondary',
  'brand.primary',
  'brand.accent',
  'brand.secondary_foreground',
  'brand.primary_foreground',
  'brand.accent_foreground',
  'brand.corner_style',
  'brand.font_pairing',
]

export const LIVE_PATCHABLE_PREVIEW_COPY_PATHS = [
  'pages./.blocks.hero.hero.0.props.title',
  'pages./.blocks.hero.hero.0.props.subtext',
  'pages./.blocks.hero.hero.0.props.buttonText',
  'pages./about-us.blocks.main.0.props.title',
  'pages./about-us.blocks.main.0.props.text',
  'pages./contact-us.blocks.main.0.props.title',
  'pages./contact-us.blocks.main.0.props.description',
  'pages./gallery.blocks.main.0.props.title',
  'pages./gallery.blocks.main.0.props.description',
  'pages./reviews.blocks.main.0.props.title',
  'pages./reviews.blocks.main.0.props.description',
]

export const LIVE_PATCHABLE_PREVIEW_HEADER_PATHS = [
  'pages./.blocks.header.brand.0.props.logoMaxHeightPx',
  'pages./.blocks.footer.footer.0.props.logoMaxHeightPx',
]

export const STOREFRONT_PREVIEW_PATCH_TARGETS = {
  heroCopy: 'hero-copy',
  aboutCopy: 'about-copy',
  contactCopy: 'contact-copy',
  galleryCopy: 'gallery-copy',
  reviewsCopy: 'reviews-copy',
  headerBannerNavCopy: 'header-banner-nav-copy',
  headerNavMenuCopy: 'header-nav-menu-copy',
}

const livePatchablePreviewPathSet = new Set([
  ...LIVE_PATCHABLE_PREVIEW_STYLE_PATHS,
  ...LIVE_PATCHABLE_PREVIEW_COPY_PATHS,
  ...LIVE_PATCHABLE_PREVIEW_HEADER_PATHS,
])

const livePatchablePreviewPathMatchers = [
  {
    target: STOREFRONT_PREVIEW_PATCH_TARGETS.heroCopy,
    matches: (path) =>
      path === 'pages./.blocks.hero.hero.0.props.title' ||
      path === 'pages./.blocks.hero.hero.0.props.subtext' ||
      path === 'pages./.blocks.hero.hero.0.props.buttonText',
  },
  {
    target: STOREFRONT_PREVIEW_PATCH_TARGETS.aboutCopy,
    matches: (path) =>
      path === 'pages./about-us.blocks.main.0.props.title' ||
      path === 'pages./about-us.blocks.main.0.props.text',
  },
  {
    target: STOREFRONT_PREVIEW_PATCH_TARGETS.contactCopy,
    matches: (path) =>
      path === 'pages./contact-us.blocks.main.0.props.title' ||
      path === 'pages./contact-us.blocks.main.0.props.description',
  },
  {
    target: STOREFRONT_PREVIEW_PATCH_TARGETS.galleryCopy,
    matches: (path) =>
      path === 'pages./gallery.blocks.main.0.props.title' ||
      path === 'pages./gallery.blocks.main.0.props.description',
  },
  {
    target: STOREFRONT_PREVIEW_PATCH_TARGETS.reviewsCopy,
    matches: (path) =>
      path === 'pages./reviews.blocks.main.0.props.title' ||
      path === 'pages./reviews.blocks.main.0.props.description',
  },
  {
    target: STOREFRONT_PREVIEW_PATCH_TARGETS.headerBannerNavCopy,
    matches: (path) =>
      /^pages\.\/\.blocks\.header\.banner\.0\.nav_items\.\d+\.(label|href)$/.test(path),
  },
  {
    target: STOREFRONT_PREVIEW_PATCH_TARGETS.headerNavMenuCopy,
    matches: (path) =>
      /^pages\.\/\.blocks\.header\.navMenu\.0\.nav_items\.\d+\.(label|href)$/.test(path),
  },
]

function getAtPath(target, path) {
  return path.reduce((value, segment) => value?.[segment], target)
}

function setAtPath(target, path, value) {
  if (!target || !Array.isArray(path) || path.length === 0) {
    return
  }

  let current = target

  for (let index = 0; index < path.length - 1; index += 1) {
    const segment = path[index]

    if (current[segment] === undefined || current[segment] === null) {
      current[segment] = typeof path[index + 1] === 'number' ? [] : {}
    }

    current = current[segment]
  }

  current[path.at(-1)] = value
}

function applyInheritedHeroLayout(value, canonicalDesign, path) {
  const nextValue = cloneDeep(value)

  if (nextValue?.[0]?.props?.layout !== INHERIT_HERO_LAYOUT) {
    return nextValue
  }

  const inheritedLayout = getAtPath(canonicalDesign, [...path, 0, 'props', 'layout'])
  const defaultLayout =
    inheritedLayout && inheritedLayout !== INHERIT_HERO_LAYOUT
      ? inheritedLayout
      : DEFAULT_HERO_LAYOUT

  nextValue[0] = {
    ...nextValue[0],
    props: {
      ...(nextValue[0]?.props ?? {}),
      layout: defaultLayout,
    },
  }

  return nextValue
}

function isPreviewRecord(value) {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

function collectChangedPreviewPaths(previousValue, nextValue, path = []) {
  if (Object.is(previousValue, nextValue)) {
    return []
  }

  if (Array.isArray(previousValue) || Array.isArray(nextValue)) {
    if (!Array.isArray(previousValue) || !Array.isArray(nextValue)) {
      return [path.join('.')]
    }

    if (previousValue.length !== nextValue.length) {
      return [path.join('.')]
    }

    return previousValue.flatMap((previousItem, index) =>
      collectChangedPreviewPaths(previousItem, nextValue[index], [...path, index])
    )
  }

  if (isPreviewRecord(previousValue) || isPreviewRecord(nextValue)) {
    const previousRecord = isPreviewRecord(previousValue) ? previousValue : {}
    const nextRecord = isPreviewRecord(nextValue) ? nextValue : {}
    const keys = new Set([...Object.keys(previousRecord), ...Object.keys(nextRecord)])

    return [...keys].flatMap((key) =>
      collectChangedPreviewPaths(previousRecord[key], nextRecord[key], [...path, key])
    )
  }

  return [path.join('.')]
}

function getStorefrontPreviewPatchTarget(path) {
  if (typeof path !== 'string' || path.length === 0) {
    return null
  }

  return livePatchablePreviewPathMatchers.find((matcher) => matcher.matches(path))?.target ?? null
}

function usesCustomHeaderNavigation(design) {
  return design?.pages?.['/']?.blocks?.header?.navMenu?.[0]?.props?.itemsMode === 'custom'
}

function isLivePatchablePreviewPath(path) {
  return livePatchablePreviewPathSet.has(path) || Boolean(getStorefrontPreviewPatchTarget(path))
}

export function doesStorefrontPreviewDraftRequireReload(previousDesign, nextDesign) {
  return getStorefrontPreviewDraftSyncOptions(previousDesign, nextDesign).reloadRequired
}

export function getStorefrontPreviewPatchTargets(previousDesign, nextDesign) {
  return getStorefrontPreviewDraftSyncOptions(previousDesign, nextDesign).patchTargets
}

export function getStorefrontPreviewDraftSyncOptions(previousDesign, nextDesign) {
  if (!previousDesign || !nextDesign) {
    return { reloadRequired: true, patchTargets: [] }
  }

  const changedPaths = collectChangedPreviewPaths(previousDesign, nextDesign)
  const patchTargets = new Set()
  let reloadRequired = false

  changedPaths.forEach((path) => {
    const patchTarget = getStorefrontPreviewPatchTarget(path)
    const requiresCanonicalNavigationReload =
      patchTarget === STOREFRONT_PREVIEW_PATCH_TARGETS.headerNavMenuCopy &&
      (usesCustomHeaderNavigation(previousDesign) || usesCustomHeaderNavigation(nextDesign))

    if (!isLivePatchablePreviewPath(path) || requiresCanonicalNavigationReload) {
      reloadRequired = true
    }

    if (patchTarget && !requiresCanonicalNavigationReload) {
      patchTargets.add(patchTarget)
    }
  })

  return { reloadRequired, patchTargets: [...patchTargets] }
}

export function getStorefrontPreviewPublicPath(previewPage) {
  return storefrontPreviewPages.find((page) => page.id === previewPage)?.publicPath ?? '/'
}

export function isStorefrontPreviewPageKey(pageKey) {
  return storefrontPreviewPageIds.has(pageKey)
}

export function getStorefrontPreviewPageKeyFromPath(publicPath) {
  if (publicPath === '/' || publicPath === '') {
    return 'home'
  }

  if (publicPath === '/about-us') {
    return 'about'
  }

  if (publicPath === '/gallery' || publicPath?.startsWith('/gallery/')) {
    return 'gallery'
  }

  if (publicPath === '/contact-us') {
    return 'contact'
  }

  if (publicPath === '/reviews') {
    return 'reviews'
  }

  return null
}

export function buildStorefrontPreviewUrl({ baseUrl, previewToken, previewPage }) {
  return buildStorefrontPreviewUrlForPublicPath({
    baseUrl,
    previewToken,
    publicPath: getStorefrontPreviewPublicPath(previewPage),
  })
}

function applyPresetThemeColors(design, themes = []) {
  if (!design || typeof design !== 'object' || Array.isArray(design)) {
    return design
  }

  const brand = design?.brand

  if (
    !brand ||
    typeof brand !== 'object' ||
    Array.isArray(brand) ||
    brand?.type !== 'preset' ||
    !brand?.preset_theme_id
  ) {
    return design
  }

  const presetTheme = themes.find((theme) => Number(theme?.id) === Number(brand?.preset_theme_id))

  if (!presetTheme?.properties) {
    return design
  }

  return {
    ...design,
    brand: {
      ...cloneDeep(brand),
      ...convertToNewThemeColors(presetTheme.properties),
    },
  }
}

function getCollectionRefId(collectionRef) {
  const rawId = collectionRef?.collection_id ?? collectionRef?.collection?.id ?? null
  const numericId = Number(rawId)

  return Number.isFinite(numericId) && numericId > 0 ? numericId : null
}

function isFeaturedCollectionBlock(block) {
  return block?.type === 'featuredCollection' || block?.type === 'featuredCollections'
}

function getFeaturedCollectionBlockCollectionId(block) {
  if (!isFeaturedCollectionBlock(block) || !Array.isArray(block?.collections)) {
    return null
  }

  return getCollectionRefId(block.collections[0])
}

const SEASONAL_FEATURED_FALLBACK_PROPS = {
  layout: 'carousel',
  size: 'sm',
  imageAlign: 'left',
  title: '',
  ctaText: '',
  ctaButtonText: '',
}

function prepareSeasonalAutomationPreview(design, defaultDesign, collections) {
  const seasonal = design?.seasonal
  if (!seasonal || design?.is_default) {
    return { design, collection: null }
  }

  const source = cloneDeep(design)
  const referencedRevision =
    source?.seasonal?.hero_mode === 'reference'
      ? source?.seasonal?.seasonal_design?.published_revision
      : null
  if (referencedRevision) {
    const storedHero = getAtPath(source, [...SECTION_OVERRIDE_PATHS.hero, 0])
    const inheritedHero = getAtPath(defaultDesign, [...SECTION_OVERRIDE_PATHS.hero, 0])
    setAtPath(source, SECTION_OVERRIDE_PATHS.hero, [
      getSeasonalReferenceHeroBlock(source, storedHero ?? inheritedHero ?? {}),
    ])
  }
  const sourceMain = source?.pages?.['/']?.blocks?.main
  const selectedBlock = Array.isArray(sourceMain)
    ? sourceMain.find((block) => isFeaturedCollectionBlock(block))
    : null
  const selectedCollectionId = getFeaturedCollectionBlockCollectionId(selectedBlock)
  const selectedCollection =
    seasonal.featured_collection_mode === 'custom' && selectedCollectionId
      ? (Array.isArray(collections) ? collections : []).find(
          (candidate) =>
            Number(candidate?.id) === selectedCollectionId &&
            isCollectionVisibleOnStorefront(candidate)
        )
      : null
  const collection =
    selectedCollection ??
    resolveAutomaticSeasonalFeaturedCollection(collections, seasonal.seasonal_tag_id)
  const hero = source?.pages?.['/']?.blocks?.hero?.hero?.[0]

  if (!collection) {
    if (hero?.props) {
      hero.props.buttonHref = '/products'
    }
    return { design: source, collection: null }
  }

  const collectionHref = `/collections/${collection.slug}`
  if (hero?.props) {
    hero.props.buttonHref = collectionHref
  }

  if (source?.section_overrides?.featured_content) {
    const inheritedBlock = defaultDesign?.pages?.['/']?.blocks?.main?.find((block) =>
      isFeaturedCollectionBlock(block)
    )
    const draftCustomStyle =
      seasonal.featured_collection_mode === 'custom' && selectedBlock ? selectedBlock.props : null
    const props =
      draftCustomStyle || seasonal.featured_style_mode === 'custom'
        ? {
            ...SEASONAL_FEATURED_FALLBACK_PROPS,
            ...(draftCustomStyle ?? seasonal.featured_style ?? {}),
          }
        : {
            ...SEASONAL_FEATURED_FALLBACK_PROPS,
            ...(inheritedBlock?.props ?? selectedBlock?.props ?? {}),
          }

    setAtPath(source, SECTION_OVERRIDE_PATHS.featured_content, [
      {
        id: `seasonal-preview-featured-${collection.id}`,
        type: 'featuredCollection',
        is_enabled: true,
        sort_order: 0,
        props,
        collections: [{ collection_id: collection.id }],
      },
    ])
  }

  if (seasonal.navigation_mode === 'automatic' && source?.section_overrides) {
    source.section_overrides.header_nav_menu = false
  }

  return { design: source, collection }
}

function promoteSeasonalCollectionNavigation(canonical, collection) {
  const navBlock = canonical?.pages?.['/']?.blocks?.header?.navMenu?.[0]
  if (!navBlock || !Array.isArray(navBlock.nav_items)) {
    return
  }

  const extractCollection = (items) => {
    let found = null
    const remaining = []

    for (const item of items) {
      if (
        item?.type === 'collection' &&
        Number(item?.collection_id ?? item?.collection?.id) === Number(collection.id)
      ) {
        found ??= item
        continue
      }

      if (Array.isArray(item?.children) && item.children.length > 0) {
        const childResult = extractCollection(item.children)
        found ??= childResult.item
        remaining.push({ ...item, children: childResult.remaining })
      } else {
        remaining.push(item)
      }
    }

    return { item: found, remaining }
  }

  const extracted = extractCollection(navBlock.nav_items)
  const existing = extracted.item
  const promoted = existing ?? {
    id: `seasonal-preview-nav-${collection.id}`,
    type: 'collection',
    collection_id: collection.id,
    label: collection.name,
    href: `/collections/${collection.slug}`,
    is_enabled: true,
    collection,
    children: [],
  }
  promoted.is_enabled = true
  const remaining = extracted.remaining
  const firstItem = remaining[0]
  const shopAllIsFirst =
    firstItem?.type === 'shop_all' ||
    (firstItem?.type === 'link' &&
      typeof firstItem?.href === 'string' &&
      firstItem.href.replace(/\/+$/, '') === '/products')

  remaining.splice(shopAllIsFirst ? 1 : 0, 0, promoted)
  const normalizeSortOrders = (items) =>
    items.map((item, index) => ({
      ...item,
      sort_order: index,
      children: Array.isArray(item?.children) ? normalizeSortOrders(item.children) : [],
    }))

  navBlock.nav_items = normalizeSortOrders(remaining)
}

function applySeasonalFeaturedCollectionOverride(canonical, design, collections = []) {
  const path = SECTION_OVERRIDE_PATHS.featured_content
  const defaultMainBlocks = getAtPath(canonical, path)
  const seasonalMainBlocks = getAtPath(design, path)

  if (!Array.isArray(seasonalMainBlocks)) {
    return
  }

  const seasonalFeaturedCollectionBlock = seasonalMainBlocks.find(
    (block) => getFeaturedCollectionBlockCollectionId(block) !== null
  )

  if (!seasonalFeaturedCollectionBlock) {
    // A blank seasonal control is an incomplete selection; keep everyday content visible.
    setAtPath(canonical, path, cloneDeep(Array.isArray(defaultMainBlocks) ? defaultMainBlocks : []))
    return
  }

  const seasonalCollectionId = getFeaturedCollectionBlockCollectionId(
    seasonalFeaturedCollectionBlock
  )
  const seasonalCollection = (Array.isArray(collections) ? collections : []).find(
    (collection) => Number(collection?.id) === seasonalCollectionId
  )

  if (seasonalCollection && !isCollectionVisibleOnStorefront(seasonalCollection)) {
    setAtPath(canonical, path, cloneDeep(Array.isArray(defaultMainBlocks) ? defaultMainBlocks : []))
    return
  }

  const remainingDefaultBlocks = (Array.isArray(defaultMainBlocks) ? defaultMainBlocks : []).filter(
    (block) => {
      const collectionId = getFeaturedCollectionBlockCollectionId(block)

      return collectionId === null || collectionId !== seasonalCollectionId
    }
  )

  setAtPath(canonical, path, [
    cloneDeep(seasonalFeaturedCollectionBlock),
    ...remainingDefaultBlocks,
  ])
}

function hydrateFeaturedCollectionReferences(design, collections = []) {
  if (!design || typeof design !== 'object' || Array.isArray(design)) {
    return design
  }

  const collectionsById = new Map(
    (Array.isArray(collections) ? collections : [])
      .map((collection) => [Number(collection?.id), collection])
      .filter(([id, collection]) => Number.isFinite(id) && collection)
  )

  if (collectionsById.size === 0) {
    return design
  }

  const pages = design?.pages

  if (!pages || typeof pages !== 'object') {
    return design
  }

  Object.values(design.pages ?? {}).forEach((page) => {
    const mainBlocks = page?.blocks?.main

    if (!Array.isArray(mainBlocks)) {
      return
    }

    mainBlocks.forEach((block) => {
      if (block?.type !== 'featuredCollection' || !Array.isArray(block?.collections)) {
        return
      }

      block.collections = block.collections.map((collectionRef) => {
        const collectionId = getCollectionRefId(collectionRef)
        const collection = collectionsById.get(collectionId)
        const nextCollectionRef = {
          ...collectionRef,
          collection_id: collectionId ?? collectionRef?.collection_id ?? null,
        }

        if (collection && isCollectionVisibleOnStorefront(collection)) {
          return {
            ...nextCollectionRef,
            collection: cloneDeep(collection),
            is_enabled: collectionRef?.is_enabled ?? true,
          }
        }

        const { collection: _collection, ...collectionRefWithoutCollection } = nextCollectionRef
        return collectionRefWithoutCollection
      })
    })
  })

  return design
}

function hydrateNavItemCollectionReferences(design, collections = []) {
  if (!design || typeof design !== 'object' || Array.isArray(design)) {
    return design
  }

  const collectionsById = new Map(
    (Array.isArray(collections) ? collections : [])
      .map((collection) => [Number(collection?.id), collection])
      .filter(([id, collection]) => Number.isFinite(id) && collection)
  )

  if (collectionsById.size === 0) {
    return design
  }

  const pages = design?.pages

  if (!pages || typeof pages !== 'object') {
    return design
  }

  Object.values(design.pages ?? {}).forEach((page) => {
    Object.values(page?.blocks?.header ?? {}).forEach((blockGroup) => {
      if (!Array.isArray(blockGroup)) {
        return
      }

      blockGroup.forEach((block) => {
        if (!Array.isArray(block?.nav_items)) {
          return
        }

        const hydrateNavItems = (navItems) =>
          navItems.map((navItem) => {
            const children = Array.isArray(navItem?.children)
              ? { children: hydrateNavItems(navItem.children) }
              : {}

            if (navItem?.type !== 'collection') {
              return { ...navItem, ...children }
            }

            const collectionId = getCollectionRefId(navItem)
            const collection = collectionsById.get(collectionId)
            const nextNavItem = {
              ...navItem,
              collection_id: collectionId ?? navItem?.collection_id ?? null,
            }

            if (collection && isCollectionVisibleOnStorefront(collection)) {
              return {
                ...nextNavItem,
                collection: cloneDeep(collection),
                ...children,
              }
            }

            const { collection: _collection, ...navItemWithoutCollection } = nextNavItem
            return { ...navItemWithoutCollection, ...children }
          })

        block.nav_items = hydrateNavItems(block.nav_items)
      })
    })
  })

  return design
}

function getProductRefId(productRef) {
  const rawId = productRef?.product_id ?? productRef?.product?.id ?? null
  const numericId = Number(rawId)

  return Number.isFinite(numericId) && numericId > 0 ? numericId : null
}

function hydrateFeaturedProductReferences(design, products = [], { cloneInput = true } = {}) {
  if (!design || typeof design !== 'object' || Array.isArray(design)) {
    return design
  }

  const productsById = new Map(
    (Array.isArray(products) ? products : [])
      .map((product) => [Number(product?.id), product])
      .filter(([id, product]) => Number.isFinite(id) && product)
  )

  const pages = design?.pages

  if (!pages || typeof pages !== 'object') {
    return design
  }

  const hydratedDesign = cloneInput ? cloneDeep(design) : design

  Object.values(hydratedDesign.pages ?? {}).forEach((page) => {
    const mainBlocks = page?.blocks?.main

    if (!Array.isArray(mainBlocks)) {
      return
    }

    mainBlocks.forEach((block) => {
      if (block?.type !== 'featuredProducts' || !Array.isArray(block?.products)) {
        return
      }

      block.products = block.products.map((productRef) => {
        const productId = getProductRefId(productRef)
        const product = productsById.get(productId)
        const nextProductRef = {
          ...productRef,
          product_id: productId ?? productRef?.product_id ?? null,
        }

        if (product) {
          return {
            ...nextProductRef,
            product: cloneDeep(product),
            is_enabled: productRef?.is_enabled ?? true,
          }
        }

        const { product: _product, ...productRefWithoutProduct } = nextProductRef
        return productRefWithoutProduct
      })
    })
  })

  return hydratedDesign
}

function hydrateStorefrontPreviewReferences(design, collections = [], products = []) {
  return hydrateFeaturedProductReferences(
    hydrateFeaturedCollectionReferences(
      hydrateNavItemCollectionReferences(design, collections),
      collections
    ),
    products,
    { cloneInput: false }
  )
}

function stripPreviewIrrelevantFields(design) {
  if (!design || typeof design !== 'object') {
    return design
  }

  delete design.schedule

  return design
}

export function composeStorefrontPreviewDesign(
  design,
  defaultDesign = null,
  themes = [],
  collections = [],
  products = []
) {
  if (!design || typeof design !== 'object') {
    return null
  }

  const seasonalPreview = prepareSeasonalAutomationPreview(design, defaultDesign, collections)
  const previewSource = seasonalPreview.design

  if (previewSource?.is_default || !defaultDesign || typeof defaultDesign !== 'object') {
    const canonical = cloneDeep(previewSource)
    if (seasonalPreview.collection && previewSource?.seasonal?.navigation_mode === 'automatic') {
      promoteSeasonalCollectionNavigation(canonical, seasonalPreview.collection)
    }

    return hydrateStorefrontPreviewReferences(
      applyPageMainBlockSortOrder(
        applyPresetThemeColors(stripPreviewIrrelevantFields(canonical), themes)
      ),
      collections,
      products
    )
  }

  const overrides = previewSource?.section_overrides ?? {}
  const hasEnabledOverride = Object.keys(SECTION_OVERRIDE_PATHS).some(
    (sectionKey) => overrides?.[sectionKey] === true
  )

  if (!hasEnabledOverride) {
    return hydrateStorefrontPreviewReferences(
      applyPageMainBlockSortOrder(
        applyPresetThemeColors(stripPreviewIrrelevantFields(cloneDeep(defaultDesign)), themes)
      ),
      collections,
      products
    )
  }

  const canonical = cloneDeep(defaultDesign)

  Object.entries(SECTION_OVERRIDE_PATHS).forEach(([sectionKey, path]) => {
    if (!overrides?.[sectionKey]) {
      return
    }

    if (sectionKey === 'featured_content') {
      applySeasonalFeaturedCollectionOverride(canonical, previewSource, collections)
      return
    }

    const value = getAtPath(previewSource, path)

    if (value === undefined) {
      return
    }

    const nextValue =
      sectionKey === 'hero' ? applyInheritedHeroLayout(value, canonical, path) : cloneDeep(value)

    setAtPath(canonical, path, nextValue)
  })
  ;[
    'id',
    'name',
    'status',
    'is_default',
    'created_from',
    'section_overrides',
    'updated_at',
  ].forEach((key) => {
    if (previewSource?.[key] !== undefined) {
      canonical[key] = cloneDeep(previewSource[key])
    }
  })

  if (seasonalPreview.collection && previewSource?.seasonal?.navigation_mode === 'automatic') {
    promoteSeasonalCollectionNavigation(canonical, seasonalPreview.collection)
  }

  return hydrateStorefrontPreviewReferences(
    applyPageMainBlockSortOrder(
      applyPresetThemeColors(stripPreviewIrrelevantFields(canonical), themes)
    ),
    collections,
    products
  )
}
